import React, { useEffect } from 'react';

import { useGetCard } from '@vlabs/api/point-api/client';
import type { CardId } from '@vlabs/api/point-api/model';
import validate from '@vlabs/shared/validators';
import { Page } from '@vlabs/uikit';
import { Button } from '@vlabs/uikit/components/controls/button/Button';
import { Input } from '@vlabs/uikit/components/controls/input';
import { useForm } from 'react-hook-form';

import st from './EditCardUserDataForm.module.sass';
import { useEditCardUserDataTranslation } from '../i18n';
import type { TPointCardUserData } from '../types';

export interface EditCardUserDataFormProps {
  cardId: CardId;
  onSubmit: (values: TPointCardUserData) => void;
  onCancel?: VoidFunction;
}

export function EditCardUserDataForm({
  cardId,
  onSubmit,
  onCancel = undefined,
}: EditCardUserDataFormProps): React.JSX.Element {
  const { t } = useEditCardUserDataTranslation();
  const getCard = useGetCard(cardId);
  const form = useForm<TPointCardUserData>({ defaultValues: { fio: '' } });
  const { reset } = form;

  useEffect(() => {
    reset({ fio: (getCard.data?.user_data?.fio as string) ?? '' });
  }, [getCard.data, reset]);

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <Page
        className={st.Page}
        title={t('title') as unknown as string}
      >
        <div className={st.InputWrapper}>
          <Input
            id="fio"
            label={t('label')}
            {...form.register('fio', {
              required: validate.required(),
              maxLength: validate.maxLength(128),
            })}
            errors={form.formState.errors}
          />
        </div>

        <div className={st.ModalFooter}>
          <div className={st.ButtonWrapper}>
            <Button
              className={st.Button}
              data-testid="submitCardEditButton"
              type="submit"
            >
              {t('submit')}
            </Button>
          </div>

          {!!onCancel && (
            <div className={st.ButtonWrapper}>
              <Button
                className={st.Button}
                data-testid="closeCardEditButton"
                kind="secondary"
                onClick={onCancel}
                variant="outlined"
              >
                {t('cancel')}
              </Button>
            </div>
          )}
        </div>
      </Page>
    </form>
  );
}
